import React, { useEffect, useMemo, useState } from "react";
import { api, formatTRY } from "@/lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Search, DownloadCloud, ChevronDown, ChevronUp, Palette, Wrench, Replace, Ruler, SprayCan, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

// Ana kategoriler ve alt kategori haritası
const GROUPS = [
  {
    key: "parca-boyama",
    label: "Parça Boyama",
    icon: SprayCan,
    color: "from-blue-500 to-blue-600",
    accent: "bg-blue-50 text-blue-800 border-blue-200",
    categories: ["Parça Boyama - Normal", "Parça Boyama - Metalik", "Parça Boyama - Sedefli"],
  },
  {
    key: "komple-boya",
    label: "Komple Boya",
    icon: Palette,
    color: "from-fuchsia-500 to-fuchsia-600",
    accent: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200",
    categories: ["Komple Boya - Normal", "Komple Boya - Metalik", "Komple Boya - Sedefli"],
  },
  {
    key: "kaporta",
    label: "Kaporta Onarım",
    icon: Wrench,
    color: "from-amber-500 to-amber-600",
    accent: "bg-amber-50 text-amber-800 border-amber-200",
    categories: ["Kaporta Onarım İşçilik", "Kaporta"],
  },
  {
    key: "parca-degisim",
    label: "Parça Değişim",
    icon: Replace,
    color: "from-orange-500 to-orange-600",
    accent: "bg-orange-50 text-orange-800 border-orange-200",
    categories: ["Parça Değişim İşçilik"],
  },
  {
    key: "sasi-olcum",
    label: "Şasi ve Ölçüm",
    icon: Ruler,
    color: "from-violet-500 to-violet-600",
    accent: "bg-violet-50 text-violet-800 border-violet-200",
    categories: ["Şasi ve Ölçüm", "Şase Düzeltme"],
  },
  {
    key: "diger",
    label: "Diğer",
    icon: Wrench,
    color: "from-slate-500 to-slate-600",
    accent: "bg-slate-100 text-slate-700 border-slate-200",
    categories: ["Boya", "Boyasız Onarım"],
  },
];

const ALL_CATEGORIES = GROUPS.flatMap((g) => g.categories);

// Araç parçaları — sihirbazda kullanılır
const CAR_PARTS = [
  "Ön Tampon", "Arka Tampon", "Kaput", "Tavan",
  "Ön Çamurluk", "Arka Çamurluk", "Ön Kapı", "Arka Kapı",
  "Bagaj Kapağı", "Marşpiyel (Tek Taraf)", "Ayna Kapağı (Takım)", "Kapı Kolu (Takım)",
  "Çamurluk Dodiği", "Spoiler", "Direk", "Panel", "Taban", "Şasi Ucu",
];

// Komple boya için araç sınıfları
const CAR_CLASSES = ["Hatchback", "Sedan", "SUV / Pickup"];

// Servis alanları — sihirbaz adım 2
const SERVICES = [
  {
    key: "boya",
    label: "Boya",
    icon: SprayCan,
    color: "from-blue-500 to-blue-600",
    requiresColor: true,
    parts: CAR_PARTS,
    // (part, color) → (name, category)
    resolve: (part, color) => ({ name: part, category: `Parça Boyama - ${color}` }),
  },
  {
    key: "komple-boya",
    label: "Komple Boya",
    icon: Palette,
    color: "from-fuchsia-500 to-fuchsia-600",
    requiresColor: true,
    parts: CAR_CLASSES,
    resolve: (part, color) => ({ name: part, category: `Komple Boya - ${color}` }),
  },
  {
    key: "kaporta",
    label: "Kaporta Onarım",
    icon: Wrench,
    color: "from-amber-500 to-amber-600",
    requiresColor: false,
    parts: CAR_PARTS,
    // Bazı parçalar "Onarımı", çoğu "Düzeltme" ekiyle geliyor — akıllı isim
    resolve: (part) => ({
      name: part === "Ön Tampon" || part === "Arka Tampon" ? "Tampon Onarımı" : `${part} Düzeltme`,
      category: "Kaporta Onarım İşçilik",
    }),
  },
  {
    key: "degisim",
    label: "Parça Değişim",
    icon: Replace,
    color: "from-orange-500 to-orange-600",
    requiresColor: false,
    parts: ["Tampon", "Çamurluk", "Kaput", "Kapı", "Bagaj Kapağı", "Marşpiyel", "Ön Panel", "Arka Panel"],
    resolve: (part) => ({ name: `${part} Değişimi`, category: "Parça Değişim İşçilik" }),
  },
  {
    key: "sasi",
    label: "Şasi / Ölçüm",
    icon: Ruler,
    color: "from-violet-500 to-violet-600",
    requiresColor: false,
    parts: ["Şasi Ölçümü", "Şasi Düzeltme", "Podye Düzeltme"],
    resolve: (part) => ({ name: part, category: "Şasi ve Ölçüm" }),
  },
  {
    key: "boyasiz",
    label: "Boyasız Onarım",
    icon: Wrench,
    color: "from-emerald-500 to-emerald-600",
    requiresColor: false,
    parts: CAR_PARTS,
    resolve: (part) => ({ name: `${part} (Boyasız Onarım)`, category: "Boyasız Onarım" }),
  },
];

const COLORS = ["Normal", "Metalik", "Sedefli"];

export default function Parts() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [expanded, setExpanded] = useState({ "parca-boyama": true });

  // Sihirbaz state
  const [wizStep, setWizStep] = useState(1); // 1=part, 2=service, 3=color, 4=confirm
  const [wizService, setWizService] = useState(null);
  const [wizPart, setWizPart] = useState("");
  const [wizColor, setWizColor] = useState("");
  const [wizPrice, setWizPrice] = useState("");
  const [wizExisting, setWizExisting] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => setItems((await api.get("/parts")).data);
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const resetWizard = () => {
    setWizStep(1);
    setWizService(null);
    setWizPart("");
    setWizColor("");
    setWizPrice("");
    setWizExisting(null);
    setEditing(null);
  };

  const openNew = () => {
    resetWizard();
    setOpen(true);
  };

  const openEdit = (p) => {
    // Var olan parçayı sihirbaz üzerinden düzenle
    resetWizard();
    setEditing(p);
    // Kategoriden servis + renk çıkar
    let svc = null;
    let color = "";
    if (p.category?.startsWith("Parça Boyama - ")) {
      svc = SERVICES.find((s) => s.key === "boya");
      color = p.category.split(" - ")[1];
    } else if (p.category?.startsWith("Komple Boya - ")) {
      svc = SERVICES.find((s) => s.key === "komple-boya");
      color = p.category.split(" - ")[1];
    } else if (p.category === "Kaporta Onarım İşçilik" || p.category === "Kaporta") {
      svc = SERVICES.find((s) => s.key === "kaporta");
    } else if (p.category === "Parça Değişim İşçilik") {
      svc = SERVICES.find((s) => s.key === "degisim");
    } else if (p.category === "Şasi ve Ölçüm" || p.category === "Şase Düzeltme") {
      svc = SERVICES.find((s) => s.key === "sasi");
    } else if (p.category === "Boyasız Onarım") {
      svc = SERVICES.find((s) => s.key === "boyasiz");
    }
    setWizService(svc);
    setWizColor(color);
    setWizPart(p.name);
    setWizPrice(String(p.price));
    setWizExisting(p);
    setWizStep(4);
    setOpen(true);
  };

  // Sihirbaz seçimlerine göre isim/kategori/mevcut kayıt hesapla
  const resolved = useMemo(() => {
    if (!wizService || !wizPart) return null;
    if (wizService.requiresColor && !wizColor) return null;
    const { name, category } = wizService.resolve(wizPart, wizColor);
    const existing = items.find((x) => x.name === name && x.category === category);
    return { name, category, existing };
  }, [wizService, wizPart, wizColor, items]);

  // Sihirbaz Adım 4'e girildiğinde önerilen fiyatı otomatik doldur
  useEffect(() => {
    if (wizStep === 4 && resolved && !editing) {
      if (resolved.existing) {
        setWizPrice(String(resolved.existing.price));
        setWizExisting(resolved.existing);
      } else {
        setWizExisting(null);
        if (!wizPrice) setWizPrice("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizStep, resolved]);

  const save = async () => {
    if (!resolved) return;
    const payload = {
      name: resolved.name,
      category: resolved.category,
      price: Number(wizPrice) || 0,
      note: editing?.note || "",
    };
    if (editing) {
      await api.put(`/parts/${editing.id}`, payload);
      toast.success("Güncellendi");
    } else if (resolved.existing) {
      await api.put(`/parts/${resolved.existing.id}`, payload);
      toast.success(`${resolved.name} güncellendi`);
    } else {
      await api.post("/parts", payload);
      toast.success(`${resolved.name} eklendi`);
    }
    setOpen(false);
    resetWizard();
    load();
  };

  const del = async () => {
    await api.delete(`/parts/${deleteId}`);
    toast.success("Silindi"); setDeleteId(null); load();
  };

  const importPriceList = async () => {
    setImporting(true);
    try {
      const r = await api.post("/parts/seed-price-list-2026");
      const { inserted, updated, total_defined } = r.data;
      toast.success(`Fiyat listesi içeri aktarıldı · ${inserted} yeni + ${updated} güncelleme (toplam ${total_defined})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İçeri aktarma başarısız");
    } finally {
      setImporting(false);
    }
  };

  const searchFiltered = useMemo(() => {
    if (!q) return items;
    const t = q.toLowerCase();
    return items.filter((p) =>
      p.name.toLowerCase().includes(t) ||
      p.category?.toLowerCase().includes(t) ||
      p.note?.toLowerCase().includes(t)
    );
  }, [items, q]);

  const grouped = useMemo(() => {
    const byGroup = {};
    for (const g of GROUPS) byGroup[g.key] = { group: g, categories: {} };
    for (const p of searchFiltered) {
      const g = GROUPS.find((x) => x.categories.includes(p.category)) || GROUPS[GROUPS.length - 1];
      const bucket = byGroup[g.key].categories;
      if (!bucket[p.category]) bucket[p.category] = [];
      bucket[p.category].push(p);
    }
    return byGroup;
  }, [searchFiltered]);

  const toggleGroup = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  // İleri butonu aktif mi?
  const canNext = () => {
    if (wizStep === 1) return !!wizPart;
    if (wizStep === 2) return !!wizService;
    if (wizStep === 3) return !!wizColor;
    return false;
  };

  const goNext = () => {
    if (wizStep === 1 && wizPart) setWizStep(2);
    else if (wizStep === 2 && wizService) {
      // Servis değişince parçayı sıfırla (farklı liste olabilir)
      if (!wizService.parts.includes(wizPart)) setWizPart(wizService.parts[0] || "");
      if (wizService.requiresColor) setWizStep(3);
      else setWizStep(4);
    } else if (wizStep === 3 && wizColor) setWizStep(4);
  };

  return (
    <div data-testid="parts-page" className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <p className="text-sm text-blue-900 font-semibold">Parça & İşçilik Fiyat Listesi</p>
          <p className="text-xs text-blue-800 mt-1">
            Fiyatlar kategoriye göre gruplandı. &quot;Yeni Parça Ekle&quot; ile adım adım (parça → alan → renk) seçim yapıp otomatik fiyat oluşturabilirsiniz.
          </p>
        </div>
        <Button
          data-testid="import-price-list-btn"
          onClick={importPriceList}
          disabled={importing}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <DownloadCloud className="w-4 h-4 mr-2" />
          {importing ? "Yükleniyor…" : "2026 Fiyat Listesini İçeri Aktar"}
        </Button>
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="relative w-full max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="parts-search"
            placeholder="Parça / işlem ara…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-white"
          />
        </div>
        <Button data-testid="add-part-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="w-4 h-4 mr-2" /> Yeni Parça Ekle
        </Button>
      </div>

      {items.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-slate-300 rounded-lg bg-slate-50">
          <p className="text-slate-700 font-semibold">Henüz parça / işlem eklenmedi.</p>
          <p className="text-slate-500 text-sm mt-1">
            &quot;2026 Fiyat Listesini İçeri Aktar&quot; ile hazır listeyi yükleyebilir veya &quot;Yeni Parça Ekle&quot; ile adım adım ekleyebilirsiniz.
          </p>
        </div>
      )}

      {/* Gruplar */}
      <div className="space-y-3">
        {GROUPS.map((g) => {
          const bucket = grouped[g.key];
          const cats = Object.keys(bucket.categories);
          if (cats.length === 0) return null;
          const totalItems = cats.reduce((s, c) => s + bucket.categories[c].length, 0);
          const isOpen = !!expanded[g.key] || !!q;
          const Icon = g.icon;
          return (
            <section
              key={g.key}
              data-testid={`group-${g.key}`}
              className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden"
            >
              <button
                type="button"
                onClick={() => toggleGroup(g.key)}
                data-testid={`group-toggle-${g.key}`}
                className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors"
              >
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${g.color} flex items-center justify-center text-white shrink-0`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-display font-bold text-slate-900">{g.label}</p>
                  <p className="text-xs text-slate-500">{totalItems} işlem · {cats.length} alt kategori</p>
                </div>
                {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </button>
              {isOpen && (
                <div className="border-t border-slate-100 divide-y divide-slate-100">
                  {cats.map((cat) => {
                    const rows = bucket.categories[cat];
                    const variant = cat.split(" - ")[1];
                    return (
                      <div key={cat} className="p-4" data-testid={`subcat-${cat}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${g.accent}`}>
                            {variant || cat}
                          </span>
                          <span className="text-xs text-slate-500">{rows.length} işlem</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {rows.map((p) => (
                            <div
                              key={p.id}
                              data-testid={`part-card-${p.id}`}
                              className="group flex items-center justify-between gap-2 border border-slate-200 rounded-md px-3 py-2 hover:border-slate-400 hover:shadow-sm transition-all"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-slate-900 truncate" title={p.name}>{p.name}</p>
                                {p.note && <p className="text-[10px] text-slate-500 truncate">{p.note}</p>}
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <span className="text-sm font-mono-num font-bold text-slate-900">{formatTRY(p.price)}</span>
                                <Button data-testid={`edit-part-${p.id}`} variant="ghost" size="icon" onClick={() => openEdit(p)} className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Pencil className="w-3.5 h-3.5 text-slate-600" />
                                </Button>
                                <Button data-testid={`delete-part-${p.id}`} variant="ghost" size="icon" onClick={() => setDeleteId(p.id)} className="h-7 w-7 opacity-0 group-hover:opacity-100 hover:bg-rose-50 transition-opacity">
                                  <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {/* Sihirbaz Diyalog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetWizard(); }}>
        <DialogContent data-testid="part-dialog" className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Parça Düzenle" : "Yeni Parça Ekle · Sihirbaz"}
            </DialogTitle>
          </DialogHeader>

          {/* Adım Göstergesi */}
          <div className="flex items-center justify-center gap-2 py-2">
            {[1, 2, 3, 4].map((s) => {
              if (s === 3 && wizService && !wizService.requiresColor) return null;
              const active = wizStep === s;
              const done = wizStep > s;
              return (
                <div key={s} className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center ${
                    done ? "bg-emerald-500 text-white" : active ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"
                  }`}>
                    {done ? <CheckCircle2 className="w-4 h-4" /> : s === 4 ? "✓" : s}
                  </div>
                  {s < 4 && <div className={`w-8 h-0.5 ${done ? "bg-emerald-500" : "bg-slate-200"}`} />}
                </div>
              );
            })}
          </div>

          <div className="space-y-4 py-2">
            {/* ADIM 1: PARÇA SEÇ */}
            {wizStep === 1 && (
              <div>
                <Label className="text-sm text-slate-700 font-semibold">1. Adım · Aracın Parçasını Seçin</Label>
                <p className="text-xs text-slate-500 mb-3">Hangi parça üzerinde işlem yapılacak?</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {CAR_PARTS.map((p) => {
                    const active = wizPart === p;
                    return (
                      <button
                        key={p}
                        type="button"
                        data-testid={`wiz-part-${p}`}
                        onClick={() => setWizPart(p)}
                        className={`px-3 py-2.5 rounded-md text-xs font-semibold border transition-all ${
                          active
                            ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                            : "bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50"
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ADIM 2: SERVİS/ALAN SEÇ */}
            {wizStep === 2 && (
              <div>
                <Label className="text-sm text-slate-700 font-semibold">2. Adım · Yapılacak İşlem Alanı</Label>
                <p className="text-xs text-slate-500 mb-3">
                  Seçili parça: <b className="text-slate-900">{wizPart}</b>. Şimdi hangi işlem yapılacak?
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {SERVICES.filter((s) => s.parts.includes(wizPart) || s.key === "komple-boya" || s.key === "sasi").map((s) => {
                    if (s.key === "komple-boya") return null; // komple boya için parça listesi araç sınıfları — ayrı akış
                    if (s.key === "sasi" && !["Şasi Ucu"].includes(wizPart)) return null;
                    const Icon = s.icon;
                    const active = wizService?.key === s.key;
                    return (
                      <button
                        key={s.key}
                        type="button"
                        data-testid={`wiz-service-${s.key}`}
                        onClick={() => setWizService(s)}
                        className={`px-3 py-3 rounded-md text-sm font-semibold border transition-all flex items-center gap-2 ${
                          active
                            ? `bg-gradient-to-br ${s.color} text-white border-transparent shadow-sm`
                            : "bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ADIM 3: RENK (sadece Boya için) */}
            {wizStep === 3 && wizService?.requiresColor && (
              <div>
                <Label className="text-sm text-slate-700 font-semibold">3. Adım · Boya Türünü Seçin</Label>
                <p className="text-xs text-slate-500 mb-3">
                  <b className="text-slate-900">{wizPart}</b> · <b>{wizService.label}</b> için renk tipini seçin.
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {COLORS.map((c) => {
                    const active = wizColor === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        data-testid={`wiz-color-${c}`}
                        onClick={() => setWizColor(c)}
                        className={`px-4 py-4 rounded-md text-sm font-semibold border transition-all ${
                          active
                            ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                            : "bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50"
                        }`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ADIM 4: ÖZET & FİYAT */}
            {wizStep === 4 && resolved && (
              <div className="space-y-4">
                <Label className="text-sm text-slate-700 font-semibold">
                  {editing ? "Düzenleme" : "Son Adım · Onay ve Fiyatlandırma"}
                </Label>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Parça / İşlem:</span>
                    <span className="font-semibold text-slate-900" data-testid="wiz-preview-name">{resolved.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Kategori:</span>
                    <span className="font-semibold text-slate-900" data-testid="wiz-preview-category">{resolved.category}</span>
                  </div>
                  {resolved.existing && !editing && (
                    <div className="mt-2 pt-2 border-t border-slate-200 flex items-center gap-2 text-xs text-amber-800 bg-amber-50 -mx-2 px-3 py-2 rounded">
                      <CheckCircle2 className="w-3.5 h-3.5 text-amber-600" />
                      Bu kayıt zaten listede var. Fiyatı güncelleyebilirsiniz.
                    </div>
                  )}
                </div>

                <div>
                  <Label>Fiyat (₺)</Label>
                  <Input
                    data-testid="wiz-price-input"
                    type="number"
                    step="0.01"
                    value={wizPrice}
                    onChange={(e) => setWizPrice(e.target.value)}
                    className="font-mono-num text-lg h-11"
                    placeholder={resolved.existing ? String(resolved.existing.price) : "Fiyat girin"}
                    autoFocus
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    {resolved.existing ? "Mevcut fiyat otomatik dolduruldu. İsterseniz değiştirin." : "Bu kombinasyon için henüz fiyat yok. Fiyat girin."}
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-row justify-between gap-2">
            <div>
              {wizStep > 1 && !editing && (
                <Button
                  type="button"
                  variant="outline"
                  data-testid="wiz-back-btn"
                  onClick={() => setWizStep((s) => {
                    if (s === 4 && wizService && !wizService.requiresColor) return 2;
                    return s - 1;
                  })}
                >
                  ← Geri
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setOpen(false); resetWizard(); }}>İptal</Button>
              {wizStep < 4 ? (
                <Button
                  data-testid="wiz-next-btn"
                  onClick={goNext}
                  disabled={!canNext()}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  İleri →
                </Button>
              ) : (
                <Button
                  data-testid="save-part-btn"
                  onClick={save}
                  disabled={!wizPrice || Number(wizPrice) <= 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {editing || resolved?.existing ? "Güncelle" : "Listeye Ekle"}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Parça silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-part" onClick={del} className="bg-rose-600 hover:bg-rose-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export { GROUPS, ALL_CATEGORIES, CAR_PARTS, SERVICES, COLORS };
